import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Laptop, LogOut, Moon, Sun, User as UserIcon } from "lucide-react";
import { Container } from "@/components/layout/Container";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { FormField } from "@/components/auth/FormField";
import { useCurrentUser, useUpdateMe } from "@/hooks/useCurrentUser";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import { LinkedParentsCard } from "@/components/settings/LinkedParentsCard";
import { BillingPanel } from "@/components/settings/BillingPanel";
import type { ThemeMode } from "@/stores/ui-store";
import type { UserUpdate } from "@/lib/api-types";
import { cn } from "@/lib/cn";
import { isParentPortalEnabled } from "@/lib/env";

// Mirrors the editable fields + validators in staxed_users_handler:
//   user_name (>=2), phone / parent_contact (Indian mobile), dob (YYYY-MM-DD),
//   school_name, city. class_id is NOT editable here -- it's tied to the
//   active subscription and set during onboarding. For students, phone and
//   school are required (the backend rejects clearing them), checked in
//   onSubmit since the requirement depends on the role.
const phoneRule = z
  .string()
  .trim()
  .optional()
  .refine((v) => !v || /^(?:\+?91)?[6-9]\d{9}$/.test(v.replace(/\s+/g, "")), {
    message: "Enter a 10-digit Indian mobile number, or leave it blank",
  });

const schema = z.object({
  user_name: z.string().trim().min(2, "Please enter your name").max(100),
  phone: phoneRule,
  dob: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || /^\d{4}-\d{2}-\d{2}$/.test(v), {
      message: "Use the date picker (YYYY-MM-DD)",
    }),
  school_name: z.string().trim().max(150).optional(),
  city: z.string().trim().max(80).optional(),
  parent_contact: phoneRule,
});

type FormValues = z.infer<typeof schema>;

const THEME_OPTIONS: { value: ThemeMode; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Laptop },
];

export function SettingsPage() {
  const navigate = useNavigate();
  const me = useCurrentUser();
  const updateMe = useUpdateMe();
  const { signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      user_name: me.data?.user_name ?? "",
      phone: me.data?.phone ?? "",
      dob: me.data?.dob ?? "",
      school_name: me.data?.school_name ?? "",
      city: me.data?.city ?? "",
      parent_contact: me.data?.parent_contact ?? "",
    },
  });

  const isStudent = me.data?.role_id === "STUDENT";

  const onSubmit = handleSubmit(async (values) => {
    if (isStudent) {
      let missing = false;
      if (!values.phone?.trim()) {
        setError("phone", { message: "Phone number is required" });
        missing = true;
      }
      if (!values.school_name?.trim()) {
        setError("school_name", { message: "School is required" });
        missing = true;
      }
      if (missing) return;
    }
    setSubmitting(true);
    try {
      // Send only editable fields; empty strings become null so the backend
      // clears the value rather than rejecting it.
      const patch: UserUpdate = {
        user_name: values.user_name.trim(),
        phone: values.phone?.trim() || null,
        dob: values.dob?.trim() || null,
        school_name: values.school_name?.trim() || null,
        city: values.city?.trim() || null,
        parent_contact: values.parent_contact?.trim() || null,
      };
      await updateMe.mutateAsync(patch);
      toast.success("Profile updated.");
    } catch (err) {
      const message =
        (err as { response?: { data?: { status_message?: string } } }).response?.data
          ?.status_message ||
        (err as { message?: string }).message ||
        "Couldn't save your changes.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <Container size="md" className="py-8 sm:py-12">
      <div className="mx-auto max-w-2xl">
        <header className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-soft">
            <UserIcon className="h-5 w-5 text-brand" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold">Settings</h1>
            <p className="text-sm text-text-muted">Manage your profile and preferences.</p>
          </div>
        </header>

        {/* Profile -------------------------------------------------------- */}
        <section className="mt-8">
          <h2 className="text-sm font-medium text-text-muted mb-3">Profile</h2>
          <Card className="p-5 sm:p-6">
            {me.isLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : (
              <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
                <FormField id="email" label="Email">
                  <Input id="email" value={me.data?.email ?? ""} disabled readOnly />
                </FormField>

                <FormField id="user_name" label="Full name" error={errors.user_name?.message}>
                  <Input
                    id="user_name"
                    autoComplete="name"
                    placeholder="Your full name"
                    aria-invalid={!!errors.user_name}
                    {...register("user_name")}
                  />
                </FormField>

                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField id="phone" label={isStudent ? "Phone" : "Phone (optional)"} error={errors.phone?.message}>
                    <Input
                      id="phone"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      placeholder="10-digit mobile number"
                      aria-invalid={!!errors.phone}
                      {...register("phone")}
                    />
                  </FormField>
                  <FormField id="dob" label="Date of birth (optional)" error={errors.dob?.message}>
                    <Input id="dob" type="date" aria-invalid={!!errors.dob} {...register("dob")} />
                  </FormField>
                </div>

                <FormField id="school_name" label={isStudent ? "School" : "School (optional)"} error={errors.school_name?.message}>
                  <Input id="school_name" placeholder="Your school" {...register("school_name")} />
                </FormField>

                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField id="city" label="City (optional)" error={errors.city?.message}>
                    <Input
                      id="city"
                      autoComplete="address-level2"
                      placeholder="Your city"
                      {...register("city")}
                    />
                  </FormField>
                  <FormField
                    id="parent_contact"
                    label="Parent contact (optional)"
                    error={errors.parent_contact?.message}
                  >
                    <Input
                      id="parent_contact"
                      type="tel"
                      inputMode="numeric"
                      placeholder="10-digit number"
                      aria-invalid={!!errors.parent_contact}
                      {...register("parent_contact")}
                    />
                  </FormField>
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <Button type="submit" loading={submitting} disabled={!isDirty}>
                    Save changes
                  </Button>
                  {isDirty ? (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => reset()}
                      disabled={submitting}
                    >
                      Cancel
                    </Button>
                  ) : null}
                </div>
              </form>
            )}
          </Card>
        </section>

        {/* Linked parents (student-first invite flow, Phase 0 of the parent
            portal) -- gated on role since only a student can invite a parent,
            and on the build-time flag per docs/PARENT_PORTAL_PLAN.md §15. */}
        {me.data?.role_id === "STUDENT" && isParentPortalEnabled() ? <LinkedParentsCard /> : null}

        {/* Appearance ----------------------------------------------------- */}
        <section className="mt-8">
          <h2 className="text-sm font-medium text-text-muted mb-3">Appearance</h2>
          <Card className="p-5 sm:p-6">
            <p className="text-sm font-medium">Theme</p>
            <p className="text-xs text-text-muted mt-1">Choose how StaxED looks on this device.</p>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {THEME_OPTIONS.map(({ value, label, icon: Icon }) => {
                const active = theme === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setTheme(value)}
                    aria-pressed={active}
                    className={cn(
                      "flex flex-col items-center gap-1.5 rounded-lg border py-3",
                      "transition-[border-color,background-color] duration-150",
                      active
                        ? "border-brand bg-brand-soft text-brand"
                        : "border-border text-text-muted hover:border-border-strong hover:text-text",
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="text-xs font-medium">{label}</span>
                  </button>
                );
              })}
            </div>
          </Card>
        </section>

        {/* Subscription & Billing (moved out of primary nav -- see Sidebar) - */}
        <section className="mt-8">
          <h2 className="text-sm font-medium text-text-muted mb-3">Subscription &amp; billing</h2>
          <BillingPanel />
        </section>

        {/* Account -------------------------------------------------------- */}
        <section className="mt-8">
          <h2 className="text-sm font-medium text-text-muted mb-3">Account</h2>
          <Card className="p-5 sm:p-6 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium">Sign out</p>
              <p className="text-xs text-text-muted mt-0.5">
                You'll need to sign in again to continue studying.
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={async () => {
                await signOut();
                navigate("/auth/login", { replace: true });
              }}
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </Card>
        </section>
      </div>
    </Container>
  );
}
